/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: number;
  title: string;
  description?: string;
  type: ToastType;
}

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
let listeners: Listener[] = [];
let nextId = 1;

const emit = () => listeners.forEach((l) => l([...toasts]));

const remove = (id: number) => {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
};

export const toaster = {
  create({ title, description, type = 'info' }: { title: string; description?: string; type?: ToastType }) {
    const id = nextId++;
    toasts = [...toasts, { id, title, description, type }];
    emit();
    setTimeout(() => remove(id), 5000);
    return id;
  },
};

const icons = {
  success: CheckCircleIcon,
  error: ExclamationTriangleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const accents: Record<ToastType, string> = {
  success: 'text-success-500',
  error: 'text-error-500',
  warning: 'text-warning-500',
  info: 'text-brand-500',
};

export const Toaster = () => {
  const [items, setItems] = useState<Toast[]>([]);

  useEffect(() => {
    const listener: Listener = (next) => setItems(next);
    listeners.push(listener);
    listener([...toasts]);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2">
      {items.map((toast) => {
        const Icon = icons[toast.type];
        return (
          <div
            key={toast.id}
            className="pointer-events-auto flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-800 dark:bg-gray-900"
          >
            <Icon className={clsx('h-5 w-5 shrink-0', accents[toast.type])} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{toast.title}</p>
              {toast.description && (
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{toast.description}</p>
              )}
            </div>
            <button
              onClick={() => remove(toast.id)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Fermer"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
